import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { RouteKey } from '@/i18n/routes'

interface ServiceCardProps {
  to: RouteKey
  title: string
  description?: string
  imageSrc?: string
  imageAlt?: string
  className?: string
}

export function ServiceCard({
  to,
  title,
  description,
  imageSrc,
  imageAlt,
  className,
}: ServiceCardProps) {
  const { t } = useTranslation()

  return (
    <LocalizedLink
      to={to}
      className={cn(
        'group flex flex-col h-full bg-cream rounded-xl overflow-hidden',
        'border border-white/50 border-t-2 border-t-transparent shadow-sm',
        'hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 hover:border-t-primary',
        'transition-all duration-300',
        className
      )}
    >
      {imageSrc ? (
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={imageSrc}
            alt={imageAlt || title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      ) : (
        <div className="aspect-[16/10] bg-primary-soft flex items-center justify-center">
          <span className="font-display text-3xl text-primary/40">{title.charAt(0)}</span>
        </div>
      )}
      <div className="flex flex-col flex-grow justify-between p-6">
        <div>
          <h3 className="font-display text-xl font-semibold text-ink group-hover:text-primary transition-colors duration-300">
            {title}
          </h3>
          {description && (
            <p className="mt-2 text-sm text-muted leading-relaxed">{description}</p>
          )}
        </div>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-ink group-hover:text-primary transition-colors">
          {t('buttons.learnMore')}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </LocalizedLink>
  )
}
